import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableSortLabel,
  TableHead,
  TableContainer,
  LinearProgress,
  TablePagination
} from '@material-ui/core';

import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault
} from 'use-query-params';
import {
  useProductEntityPrefix,
  useFindProductVerifications
} from '../Product.hooks';
import {
  BaseProductEntityTableProps,
  ProductComponent,
  ProductVerification
} from '../Product.interface';
import { AppSnackbar } from '@sierralabs/react-material-ui';
import { getProductEntityName } from '../Product.config';
import PageTemplateCardOver from '../../../common/page-template/PageTemplateCardOver';
import { stripHtml } from '../../../common/formatter';

const VerificationsTable: React.FC<BaseProductEntityTableProps> = (
  props: BaseProductEntityTableProps
) => {
  const { productId, apiParams, fetchRef } = props;
  const history = useHistory();
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);

  const {
    productVerificationList,
    isLoadingProductVerifications,
    loadingProductVerificationsError,
    findProductVerifications
  } = useFindProductVerifications(productId);

  const findProductVerificationsWithParams = useCallback(() => {
    findProductVerifications({ page, limit, order, direction, ...apiParams });
  }, [findProductVerifications, page, limit, order, direction, apiParams]);

  useEffect(() => {
    if (fetchRef) {
      fetchRef.current = findProductVerificationsWithParams;
    }
    findProductVerificationsWithParams();
  }, [fetchRef, findProductVerificationsWithParams]);

  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.Verification
  );

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <PageTemplateCardOver>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'productVerification.id'}
                  direction={getSortDirection('productVerification.id')}
                  onClick={() => handleSort('productVerification.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productVerification.title'}
                  direction={getSortDirection('productVerification.title')}
                  onClick={() => handleSort('productVerification.title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productVerification.description'}
                  direction={getSortDirection(
                    'productVerification.description'
                  )}
                  onClick={() => handleSort('productVerification.description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productVerification.createdBy'}
                  direction={getSortDirection('productVerification.createdBy')}
                  onClick={() => handleSort('productVerification.createdBy')}
                >
                  Author
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.user'}
                  direction={getSortDirection('task.user')}
                  onClick={() => handleSort('task.user')}
                >
                  Assignee
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'components.name'}
                  direction={getSortDirection('components.name')}
                  onClick={() => handleSort('components.name')}
                >
                  Component
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'release.name'}
                  direction={getSortDirection('release.name')}
                  onClick={() => handleSort('release.name')}
                >
                  Release
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingProductVerifications && (
              <TableRow>
                <TableCell colSpan={7} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {productVerificationList?.data?.map(
              (verification: ProductVerification) => (
                <TableRow
                  hover
                  key={verification.id}
                  onClick={() =>
                    history.push(
                      `/portal/products/${productId}/verifications/${verification.id}`
                    )
                  }
                >
                  <TableCell>{prefix + verification.id}</TableCell>
                  <WrappedTableCell>{verification.title}</WrappedTableCell>
                  <WrappedTableCell>
                    <div className='truncate'>
                      {stripHtml(verification.description || '')}
                    </div>
                  </WrappedTableCell>
                  <TableCell>
                    {`${verification.task.createdBy?.firstName} ${verification.task.createdBy?.lastName}`}
                  </TableCell>
                  <TableCell>
                    {verification.task.user
                      ? `${verification.task.user?.firstName} ${verification.task.user.lastName}`
                      : 'No User Assigned'}
                  </TableCell>
                  <TableCell>
                    <TaskStatusLabel task={verification.task} />
                  </TableCell>
                  <TableCell>
                    {verification.components.length !== 0
                      ? verification.components
                          .map((component: ProductComponent) => component.name)
                          .join(', ')
                      : 'No Components'}
                  </TableCell>
                  <TableCell>
                    {verification.release
                      ? verification.release.name
                      : 'No Release'}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={productVerificationList?.total || 0}
        rowsPerPage={limit}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setLimit(+event.target.value);
          setPage(0);
        }}
      />
      <AppSnackbar
        severity='error'
        open={Boolean(loadingProductVerificationsError)}
      >
        There was an error loading the list of{' '}
        {getProductEntityName('verification')}.
      </AppSnackbar>
    </PageTemplateCardOver>
  );
};

export default VerificationsTable;
